import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface EmailConfig {
  from_name: string;
  from_email: string;
  site_url: string;
  logo_url: string;
  provider: string;
}

export interface EmailHealth {
  sent_24h: number;
  failed_24h: number;
  sent_7d: number;
  failed_7d: number;
  success_rate: number;
  total_all_time: number;
}

export interface EmailLog {
  id: string;
  to_email_masked: string;
  template: string;
  status: string;
  error_message: string | null;
  created_at: string;
}

const DEFAULT_EMAIL_CONFIG: EmailConfig = {
  from_name: 'OneForm',
  from_email: 'noreply@oneform.com.br',
  site_url: 'https://oneform.com.br',
  logo_url: '',
  provider: 'resend',
};

export function useAdminEmailConfig() {
  const [config, setConfig] = useState<EmailConfig>(DEFAULT_EMAIL_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      const { data } = await supabase.rpc('get_platform_setting', { p_key: 'email' });
      if (data) setConfig(data as EmailConfig);
      setLoading(false);
    };
    fetchConfig();
  }, []);

  const save = async (newConfig: EmailConfig) => {
    setSaving(true);
    setError(null);
    const { error: e } = await supabase.rpc('admin_set_platform_setting', {
      p_key: 'email',
      p_value: newConfig as unknown as Record<string, unknown>,
    });
    if (e) setError(e.message);
    else setConfig(newConfig);
    setSaving(false);
    return { error: e?.message || null };
  };

  return { config, loading, saving, error, save, setConfig };
}

export function useEmailHealth() {
  const [health, setHealth] = useState<EmailHealth | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHealth = async () => {
      const { data } = await supabase.rpc('admin_get_email_health');
      if (data) setHealth(data);
      setLoading(false);
    };
    fetchHealth();
  }, []);

  return { health, loading };
}

export function useEmailLogs({ template, status, page = 1 }: { template?: string | null; status?: string | null; page?: number }) {
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      const { data } = await supabase.rpc('admin_list_email_logs', {
        p_template: template || null,
        p_status: status || null,
        p_page: page,
        p_per_page: 50,
      });
      if (data) { setLogs(data.logs ?? []); setTotal(data.total ?? 0); }
      setLoading(false);
    };
    fetchLogs();
  }, [template, status, page]);

  return { logs, total, loading };
}
